import React, { FC, useEffect, useRef } from 'react';
import MonacoEditor from 'react-monaco-editor';

import clx from 'classnames';
import { editor as globalEditor, useMonacoTheme } from 'client/lib/monaco';

import styles from './JsonEditor.module.scss';

interface Props {
    value: string;
    onChange: (next: string) => void;
    readOnly?: boolean;
    minHeight?: number;
    error?: boolean;
}

/**
 * Single-purpose JSON editor: Monaco with the `json` language, no minimap, auto-layout.
 * Built-in JSON parser surfaces red squiggles for syntactically invalid input. The component
 * deliberately stays small — heavier features (formatting actions, schema validation) live in
 * `GranetCodeEditor` and aren't reused because the two pages keyboard-bind different shortcuts.
 */
export const JsonEditor: FC<Props> = ({ value, onChange, readOnly, minHeight = 140, error }) => {
    const editorRef = useRef<globalEditor.IStandaloneCodeEditor | null>(null);

    const isEmpty = value.length === 0;
    useEffect(() => {
        // Recompute layout when value flips between empty/non-empty — Monaco needs an explicit
        // layout call once the container changes size after mount.
        const id = window.setTimeout(() => editorRef.current?.layout(), 0);
        return () => window.clearTimeout(id);
    }, [isEmpty]);

    return (
        <div className={clx(styles.AliceExternalAppJsonEditor, !!error && styles.AliceExternalAppJsonEditor_error)}>
            <MonacoEditor
                value={value}
                language="json"
                theme={useMonacoTheme()}
                height={minHeight}
                onChange={(v) => onChange(v ?? '')}
                editorDidMount={(ed) => {
                    editorRef.current = ed;
                }}
                options={{
                    readOnly,
                    minimap: { enabled: false },
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    fontSize: 12,
                    tabSize: 2,
                    wordWrap: 'on',
                    folding: false,
                    renderLineHighlight: 'none',
                    scrollbar: {
                        vertical: 'auto',
                        horizontal: 'auto',
                    },
                }}
            />
        </div>
    );
};
